import AARDatePicker from "@/components/AARDatePicker";
import AARTextArea from "@/components/AARTextArea";
import AARTextField from "@/components/AARTextField";
import BaseDrawer from "@/components/BaseDrawer";
import { LookupValueMapping } from "@/constants/lookups";
import { queryKeys } from "@/constants/queryKeyFactory";
import { useAllLookups } from "@/hooks/useLookups";
import { useCreateRequest } from "@/hooks/useRequests";
import { useUserGroupsWithCompanyInfo } from "@/hooks/useUserGroups";
import { ApiResponse } from "@/interfaces/api.interface";
import { ICompanyWithUsers } from "@/interfaces/company.interface";
import { IArea, ILevel } from "@/interfaces/level.interface";
import { ILookup } from "@/interfaces/lookups.interface";
import { FileWithPreview, IRequest } from "@/interfaces/request.interface";
import { queryClient } from "@/lib/queryClient";
import { reqSvc } from "@/services/request.service";
import { useAppSelector } from "@/store/hooks/hooks";
import { hideLoader, showLoader } from "@/store/slices/loaderSlice";
import { RootState, store } from "@/store/store";
import { buildFilters } from "@/utils/kendo";
import ToastService from "@/utils/toast";
import { ComboBox, MultiSelect } from "@progress/kendo-react-dropdowns";
import { useEffect, useState } from "react";
import "react-image-crop/dist/ReactCrop.css";
import FileUploader from "./FileUploader";
import { Info } from "lucide-react";

interface IProps {
  readOnlyMode: boolean;
  companies: ApiResponse<ICompanyWithUsers[]> | undefined;
  showSubmissionDrawer: boolean;
  setShowSubmissionDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  setShowDetailsDrawer: React.Dispatch<React.SetStateAction<boolean>>;

  selectedArea?: IArea;
  selectedLevel?: ILevel | null;
  setError: (error: string | null) => void;
  error: string | null;
}

interface IGridData {
  id: string;
  company: { id: string; name: string };
  activities: string[];
  user: { userId: string; name: string };
}

function SubmissionDrawer({
  companies,
  showSubmissionDrawer,
  setShowSubmissionDrawer,
  setShowDetailsDrawer,
  selectedArea,
  selectedLevel,
  setError,
  error,
  ...drawerProps
}: IProps) {
  const createRequestMutation = useCreateRequest({
    showLoader: false,
  });
  const [data, setData] = useState<IGridData[]>([]);
  const [focusOutGrid, setFocusOutGrid] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<FileWithPreview[]>([]);
  const [submissionData, setSubmissionData] = useState<Partial<IRequest>>({
    companies: [],
  });
  const [invalidFields, setInvalidFields] = useState<{
    [key: string]: boolean;
  }>({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!showSubmissionDrawer) {
      setIsSubmitting(false);
    }
  }, [showSubmissionDrawer]);

  const validateSave = () => {
    // 1) Make sure the single assignment is filled out
    const hasValidAssignment =
      selectedCompany !== null &&
      selectedActivities.length > 0 &&
      selectedUserGroup !== null;

    // 2) All required simple fields
    const hasValidDates = !!submissionData.from && !!submissionData.to;
    const hasValidSummary = submissionData.workSummary?.trim().length! > 0;

    // // 3) At least one attachment either already URL’d or freshly uploaded
    // const hasAttachments =
    //   (submissionData.attachmentUrls?.length ?? 0) > 0 ||
    //   uploadedFiles.length > 0;

    // 4) No invalidFields flagged
    const allFieldsValid = !Object.values(invalidFields).some(Boolean);

    return (
      hasValidAssignment &&
      hasValidDates &&
      hasValidSummary &&
      // hasAttachments &&
      allFieldsValid
    );
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setSubmissionData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const { selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );

  const { data: activitiesData } = useAllLookups(
    {
      filter: buildFilters(selectedProject?.id, undefined, false, [
        { field: "type", operator: "eq", value: LookupValueMapping.Activities },
      ]),
    },
    { enabled: true }
  );

  const { data: userGroupsData } = useUserGroupsWithCompanyInfo(
    selectedProject?.id || "",
    selectedLevel?.id || "",
    { 
      enabled: showSubmissionDrawer && !!selectedLevel?.id && !!selectedProject?.id 
    }
  );

  const handleSubmit = () => {
    setIsSubmitting(true);
    store.dispatch(showLoader());
    // Prepare the request payload based on IRequest interface
    const requestPayload: IRequest = {
      areaId: selectedArea?.id,
      from: submissionData.from,
      to: submissionData.to,
      workSummary: submissionData.workSummary,
      description: submissionData.description,
      wirNumber: submissionData.wirNumber,
      companies:
        selectedCompany && selectedUserGroup
          ? [
              {
                id: selectedCompany.id,
                name: selectedCompany.name,
                activities: selectedActivities.map((act: any) => ({
                  name: act.value,
                  isApplicable: false,
                  wirNo: "",
                })),
                user: { userId: selectedUserGroup.id, name: selectedUserGroup.name },
              },
            ]
          : [],
      attachmentUrls: [],
    };

    createRequestMutation.mutate(requestPayload, {
      onSuccess: (response) => {
        const res = response.data as IRequest;
        if (res?.id) {
          reqSvc
            .uploadAttachments(res.id, uploadedFiles)
            .then((res) => {
              ToastService.success("Request submitted successfully");
              setShowSubmissionDrawer(false);
              queryClient.invalidateQueries({
                queryKey: queryKeys.request.root,
              });
              queryClient.invalidateQueries({
                queryKey: queryKeys.levels.root,
              });
            })
            .finally(() => {
              setSelectedCompany(null);
              setSelectedActivities([]);
              setSelectedUserGroup(null);

              setData([]);
              setSubmissionData({});
              setUploadedFiles([]);
              store.dispatch(hideLoader());
            });

          return;
        }
      },
      onSettled: () => {
        store.dispatch(hideLoader());
        setIsSubmitting(false);
      },
    });
  };

  // One‐time lookups:
  const activityLookups = activitiesData?.data?.filter((a) => a.isActive) || [];

  // Local form state for the single assignment
  const [selectedCompany, setSelectedCompany] =
    useState<ICompanyWithUsers | null>(null);
  const [selectedActivities, setSelectedActivities] = useState<ILookup[]>([]);
  const [selectedUserGroup, setSelectedUserGroup] = useState<{
    id: string;
    name: string;
  } | null>(null);
  
  // Filter states for ComboBoxes
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [userGroupFilter, setUserGroupFilter] = useState<string>("");

  // Whenever company changes, clear acts + user group
  useEffect(() => {
    setSelectedActivities([]);
    setSelectedUserGroup(null);
    setUserGroupFilter("");
  }, [selectedCompany]);

  const renderDrawerContent = () => (
    <div className="flex flex-col w-full h-full overflow-y-auto max-h-[calc(100vh-200px)]">
      <div className="w-full border border-gray-300 rounded p-4 mb-4">
        <h3 className="text-lg mb-2 text-[#2B3674] ">Subcontractor Details</h3>
        <div className="grid grid-cols-3 gap-4">
          {/* Company */}
          <ComboBox
            data={(companies?.data || [])
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .filter((company) =>
                company.name.toLowerCase().includes(companyFilter.toLowerCase())
              )}
            textField="name"
            dataItemKey="id"
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.value)}
            onFilterChange={(e) => setCompanyFilter(e.filter.value)}
            placeholder="Select company"
            filterable
            clearButton={false}
          />

          {/* Activities */}
          <MultiSelect
            data={
              // only activities that company supports
              selectedCompany
                ? activityLookups.filter((a) =>
                    selectedCompany.activities.includes(a.id!)
                  )
                : []
            }
            textField="value"
            dataItemKey="id"
            value={selectedActivities}
            onChange={(e) => setSelectedActivities(e.value)}
            placeholder="Select activities"
            filterable
          />

          {/* User Group */}
          <div className="flex items-center gap-2">
            <ComboBox
              data={
                (userGroupsData?.data || [])
                  .filter((ug) => {
                    // Filter groups that have at least one user with the selected company
                    if (!selectedCompany) return false;
                    return ug.users.some(user => user.companyId === selectedCompany.id);
                  })
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .filter((ug) =>
                    ug.name.toLowerCase().includes(userGroupFilter.toLowerCase())
                  )
              }
              textField="name"
              dataItemKey="id"
              value={selectedUserGroup}
              onChange={(e) => setSelectedUserGroup(e.value)}
              onFilterChange={(e) => setUserGroupFilter(e.filter.value)}
              placeholder="Select user group"
              filterable
              disabled={!selectedCompany}
              style={{ flex: 1 }}
            />
            {selectedUserGroup && (
              <div className="relative group">
                <Info 
                  className="text-blue-500 cursor-help hover:text-blue-600 transition-colors" 
                  size={20}
                />
                <div className="absolute top-full right-0 mt-2 hidden group-hover:block w-64" style={{ zIndex: 9999 }}>
                  <div className="bg-gray-800 text-white text-sm rounded-lg shadow-xl p-3">
                    <div className="absolute bottom-full right-4 mb-[-1px]">
                      <div className="border-8 border-transparent border-b-gray-800"></div>
                    </div>
                    <div className="font-semibold mb-2">Users to be assigned:</div>
                    <ul className="list-disc pl-4 space-y-1">
                      {(userGroupsData?.data || [])
                        .find((ug) => ug.id === selectedUserGroup.id)
                        ?.users.filter((user) => user.companyId === selectedCompany?.id)
                        .map((user) => (
                          <li key={user.id}>{user.name}</li>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="border border-gray-300 rounded p-4 mb-4">
        <h3 className="text-lg mb-2 text-[#2B3674] ">Request Details</h3>

        <div className="grid grid-cols-2 gap-x-2">
          <div>
            <AARTextField
              name="wirNumber"
              value={submissionData?.wirNumber || ""}
              onChange={handleChange}
              onValidate={(name, isValid) =>
                setInvalidFields((prev) => ({
                  ...prev,
                  [name]: !isValid,
                }))
              }
              onFocus={() => {
                setFocusOutGrid(true);
              }}
              label="WIR No."
              required={true}
              placeholder="Enter WIR No."
              disabled={false}
              error="Invalid WIR No."
              regex={/^[a-zA-Z0-9]+$/}
            />
          </div>
          <div className="grid grid-cols-2 gap-x-2">
            <AARDatePicker
              name="from"
              onFocus={() => {
                setFocusOutGrid(true);
              }}
              value={submissionData?.from || null}
              onChange={handleChange}
              onValidate={(name, isValid) =>
                setInvalidFields((prev) => ({
                  ...prev,
                  [name]: !isValid,
                }))
              }
              label="From"
              required={true}
              placeholder="dd/mm/yyyy"
              disabled={false}
              time={false}
              min={new Date()}
            />
            <AARDatePicker
              name="to"
              onFocus={() => {
                setFocusOutGrid(true);
              }}
              value={submissionData?.to || null}
              onChange={handleChange}
              onValidate={(name, isValid) =>
                setInvalidFields((prev) => ({
                  ...prev,
                  [name]: !isValid,
                }))
              }
              disabled={!submissionData?.from}
              min={submissionData?.from}
              label="To"
              required={true}
              placeholder="dd/mm/yyyy"
              time={false}
            />
          </div>
        </div>
        <div className="mt-4">
          <AARTextArea
            onFocus={() => {
              setFocusOutGrid(true);
            }}
            name="workSummary"
            value={submissionData?.workSummary || ""}
            onChange={handleChange}
            onValidate={(name, isValid) =>
              setInvalidFields((prev) => ({
                ...prev,
                [name]: !isValid,
              }))
            }
            label="Work Summary"
            required={true}
            placeholder="Enter Work Summary"
            disabled={false}
            error="Invalid Summary"
          />
        </div>
        <div className="mt-4 mb-4">
          <AARTextArea
            onFocus={() => {
              setFocusOutGrid(true);
            }}
            name="description"
            value={submissionData?.description || ""}
            onChange={handleChange}
            onValidate={(name, isValid) =>
              setInvalidFields((prev) => ({
                ...prev,
                [name]: !isValid,
              }))
            }
            label="Description"
            required={false}
            placeholder="Enter Description"
            disabled={false}
            error="Invalid Description"
          />
        </div>
        <FileUploader
          required={false}
          label="Attach Images/Files"
          onFilesChange={setUploadedFiles}
          files={uploadedFiles}
          maxFileSize={5}
          setError={setError}
          error={error}
        />
      </div>
    </div>
  );

  return (
    <BaseDrawer
      showIcon={true}
      isOpen={showSubmissionDrawer}
      onClose={() => {
        setSelectedCompany(null);
        setSelectedActivities([]);
        setSelectedUserGroup(null);
        setData([]);
        setSubmissionData({});
        setUploadedFiles([]);
        setShowSubmissionDrawer(false);
        setError(null);
        setIsSubmitting(false);
        setCompanyFilter("");
        setUserGroupFilter("");
      }}
      showBackButton={true}
      onBackButtonClick={() => {
        setSelectedCompany(null);
        setSelectedActivities([]);
        setSelectedUserGroup(null);
        setShowSubmissionDrawer(false);
        setShowDetailsDrawer(true);
        setData([]);
        setUploadedFiles([]);
        setError(null);
        setIsSubmitting(false);
        setCompanyFilter("");
        setUserGroupFilter("");
      }}
      title={`${selectedArea?.name ? `${selectedArea.name} - ` : ""} New AAR`}
      primaryActionText={"Submit"}
      onPrimaryAction={handleSubmit}
      primaryActionDisabled={!validateSave() || isSubmitting}
      maxW={"70vw"}
      // status={"Available"}
      {...drawerProps}
    >
      {renderDrawerContent()}
    </BaseDrawer>
  );
}

export default SubmissionDrawer;
